import { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { CorePropsInterfaces, CoreInterfaces, DTOs } from "src/core/Models";
import * as Constants from "../../../core/Constants";
import { AppContext } from "src/contexts/AppContext";
import { useSnackBar } from "src/contexts/SnackbarContext";
import {
  areAllFieldsCompleted,
  createEmptyClient,
  fetchCustomerContactPersons,
  findQuestionInService,
  formatSwedishPostalCodeInAddressLine,
  getClientTypeFromUpsales,
  getCompanyAddressCountry,
  isClientDetailsDataFilled,
  isFinalorOutdatedVersion,
  searchCompaniesByOrgNr,
} from "src/utils";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import AddHomeIcon from "@mui/icons-material/AddHome";
import SearchIcon from "@mui/icons-material/Search";
import GtRemoveHomeIcon from "src/components/Common/GtRemoveHomeIcon";
import GtDebouncedInput from "src/components/Common/GtDebouncedInput";
import GtDialog from "src/components/Common/GtDialog";
import ClientContactPicker from "./ClientContactPicker";
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlinedBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { useTranslation } from "react-i18next";

function QuestionClientDetails({
  question,
  isDisabled,
  onChange,
}: CorePropsInterfaces.QuestionInputProps) {
  const { globalState, dispatch } = useContext(AppContext);
  const lastVersionChangedDate =
    globalState.currentConfiguration.lastModifiedDate;
  const isFinalOrOutdatedVersion = isFinalorOutdatedVersion(globalState);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [contactPersonToDeleteUuid, setContactPersonToDeleteUuid] =
    useState<string>();
  const [searchingClientUuid, setSearchingClientUuid] = useState<string>();
  const [upsalesCompanyMatches, setUpsalesCompanyMatches] = useState<{
    clientUuid: string;
    anchorEl: HTMLElement;
    companies: Array<CoreInterfaces.CompanyDetails>;
  }>();
  const [upsalesContactsByCompanyId, setUpsalesContactsByCompanyId] = useState<
    Record<number, Array<CoreInterfaces.ContactPersonItem>>
  >({});
  const requestedCompanyIds = useRef(new Set<number>());
  const { t } = useTranslation();
  const { showSnackBar } = useSnackBar();

  const userDetails =
    Array.isArray(question.data.userValue) && question.data.userValue.length > 0
      ? (question.data.userValue as CoreInterfaces.ClientDetails[])
      : [createEmptyClient(globalState, true)];

  const firstClientType = userDetails[0].ClientType;
  const canAddClient =
    userDetails.length > 1 ||
    (firstClientType !== Constants.ClientType.PrivatePerson &&
      firstClientType !== Constants.HelpfulConstants.PleaseSelect);

  const linkedCompanyIdsKey = userDetails
    .slice(1)
    .map((client) => client.UpsalesCompanyId)
    .filter((companyId) => !!companyId)
    .join(",");

  useEffect(() => {
    linkedCompanyIdsKey
      .split(",")
      .filter((companyId) => !!companyId)
      .map(Number)
      .forEach((companyId) => {
        if (requestedCompanyIds.current.has(companyId)) {
          return;
        }
        requestedCompanyIds.current.add(companyId);
        fetchCustomerContactPersons(
          Constants.APIPath.ContactPersonsForGeneralInfo.replace(
            "{upsalescustomerid}",
            `${companyId}`
          )
        )
          .then((contacts) =>
            setUpsalesContactsByCompanyId((previous) => ({
              ...previous,
              [companyId]: contacts ?? [],
            }))
          )
          // Contacts are only a convenience; manual entry still works, so allow a retry later.
          .catch(() => requestedCompanyIds.current.delete(companyId));
      });
  }, [linkedCompanyIdsKey]);

  const isDigitalSigning =
    findQuestionInService(
      globalState.services.find(
        (service) =>
          service.data.code === Constants.ServiceCode.GeneralInformation
      ),
      Constants.GeneralInformationQuestion.Q0017
    )?.data.userValue === Constants.YesNo.Yes;

  const isAllDataFilled = areAllFieldsCompleted(question);
  const addSignee = (clientIndex: number) => {
    if (!isFinalOrOutdatedVersion) {
      const updatedUserDetails = [...userDetails];
      if (!Array.isArray(updatedUserDetails[clientIndex].Signees)) {
        updatedUserDetails[clientIndex].Signees = [];
      }

      const updatedSignees = [
        ...updatedUserDetails[clientIndex].Signees,
        {
          FirstName: "",
          LastName: "",
          Email: "",
          SigningMethod: Constants.SigningMethod.BankIDSE,
        },
      ];

      updatedUserDetails[clientIndex].Signees = updatedSignees;

      dispatch({
        type: Constants.AppStateActions.UpdateClientDetailsValue,
        payload: {
          contactPersonUuid: updatedUserDetails[clientIndex].Uuid,
          property: "Signees",
          value: updatedUserDetails[clientIndex].Signees,
        },
      });
    }
  };

  useEffect(() => {
    if (
      userDetails.length > 1 &&
      userDetails[0].ClientType === Constants.ClientType.PrivatePerson
    ) {
      userDetails.slice(1).forEach((contactPerson) => {
        dispatch({
          type: Constants.AppStateActions.DeleteClientDetails,
          payload: {
            contactPersonToDeleteUuid: contactPerson.Uuid,
          },
        });
      });
      userDetails[0].Signees = [
        {
          FirstName: "",
          LastName: "",
          Email: "",
          SigningMethod: Constants.SigningMethod.BankIDSE,
        },
      ];
    }
  }, [userDetails]);

  const updateSignee = (
    clientIndex: number,
    signeeIndex: number,
    field: string,
    value: string
  ) => {
    if (!isFinalOrOutdatedVersion) {
      const updatedUserDetails = [...userDetails];

      if (!Array.isArray(updatedUserDetails[clientIndex].Signees)) {
        updatedUserDetails[clientIndex].Signees = [];
      } else {
        updatedUserDetails[clientIndex].Signees = [
          ...updatedUserDetails[clientIndex].Signees,
        ];
      }

      updatedUserDetails[clientIndex].Signees[signeeIndex] = {
        ...updatedUserDetails[clientIndex].Signees[signeeIndex],
        [field]: value,
      };

      dispatch({
        type: Constants.AppStateActions.UpdateClientDetailsValue,
        payload: {
          contactPersonUuid: updatedUserDetails[clientIndex].Uuid,
          property: "Signees",
          value: updatedUserDetails[clientIndex].Signees,
        },
      });
    }
  };

  const updateGTSignee = (
    clientIndex: number,
    field: string,
    value: string
  ) => {
    if (!isFinalOrOutdatedVersion) {
      const updatedUserDetails = [...userDetails];

      updatedUserDetails[clientIndex].GTSignee = {
        ...updatedUserDetails[clientIndex].GTSignee,
        [field]: value,
      };

      dispatch({
        type: Constants.AppStateActions.UpdateClientDetailsValue,
        payload: {
          contactPersonUuid: updatedUserDetails[clientIndex].Uuid,
          property: "GTSignee",
          value: updatedUserDetails[clientIndex].GTSignee,
        },
      });
    }
  };

  const deleteSignee = (clientIndex: number, signeeIndex: number) => {
    if (!isFinalOrOutdatedVersion) {
      const updatedUserDetails = [...userDetails];
      const updatedSignees = updatedUserDetails[clientIndex].Signees.filter(
        (_, index) => index !== signeeIndex
      );

      updatedUserDetails[clientIndex].Signees = updatedSignees;

      dispatch({
        type: Constants.AppStateActions.UpdateClientDetailsValue,
        payload: {
          contactPersonUuid: updatedUserDetails[clientIndex].Uuid,
          property: "Signees",
          value: updatedUserDetails[clientIndex].Signees,
        },
      });
    }
  };

  const deleteContactPerson = () => {
    if (!isFinalOrOutdatedVersion) {
      dispatch({
        type: Constants.AppStateActions.DeleteClientDetails,
        payload: {
          contactPersonToDeleteUuid,
        },
      });
    }
  };

  const deleteDialogDTO: DTOs.GtDialogDTO = {
    data: {
      title: t("General.ClientDetails.DeleteClient"),
      maxWidth: "xs",
      leftButtons: [],
      rightButtons: [
        <Button
          onClick={() => setIsModalVisible(!isModalVisible)}
          color="inherit"
          variant="contained"
          key="cancelAction"
        >
          {t("General.Cancel")}
        </Button>,
        <Button
          onClick={() => {
            deleteContactPerson();
            setIsModalVisible(!isModalVisible);
          }}
          color="tertiary"
          variant="contained"
          key="delecteLicense"
        >
          {t("General.ClientDetails.DeleteClient")}
        </Button>,
      ],
    },
    api: {
      onClose: () => setIsModalVisible(!isModalVisible),
    },
    state: {
      isOpen: isModalVisible,
      isFullWidth: true,
    },
  };

  const updateContactPersonProperty = ({
    value,
    contactPersonUuid,
    property,
    signeeIndex,
  }: {
    value: unknown;
    contactPersonUuid: string;
    property: keyof CoreInterfaces.ClientDetails;
    signeeIndex?: number;
  }): void => {
    dispatch({
      type: Constants.AppStateActions.UpdateClientDetailsValue,
      payload: {
        contactPersonUuid,
        property,
        value,
        signeeIndex,
      },
    });
  };

  const getUpsalesContacts = (
    client: CoreInterfaces.ClientDetails,
    clientIndex: number
  ): Array<CoreInterfaces.ContactPersonItem> => {
    if (clientIndex === 0) {
      return globalState.remoteData?.customerContactPersons ?? [];
    }
    return client.UpsalesCompanyId
      ? upsalesContactsByCompanyId[client.UpsalesCompanyId] ?? []
      : [];
  };

  const isSameContact = (
    signee: CoreInterfaces.ClientSignee,
    contact: CoreInterfaces.ContactPersonItem
  ): boolean =>
    contact.email
      ? signee?.Email?.trim().toLowerCase() === contact.email.toLowerCase()
      : signee?.FirstName === contact.firstName &&
        signee?.LastName === contact.lastName;

  const toSigneeContactDetails = (
    contact: CoreInterfaces.ContactPersonItem
  ) => ({
    FirstName: contact.firstName ?? "",
    LastName: contact.lastName ?? "",
    Email: contact.email ?? "",
  });

  const addSigneeFromUpsales = (
    clientIndex: number,
    contact: CoreInterfaces.ContactPersonItem
  ) => {
    if (isFinalOrOutdatedVersion) {
      return;
    }
    const client = userDetails[clientIndex];
    const signees = Array.isArray(client.Signees) ? [...client.Signees] : [];
    const emptySigneeIndex = signees.findIndex(
      (signee) =>
        !signee.FirstName?.trim() &&
        !signee.LastName?.trim() &&
        !signee.Email?.trim()
    );
    if (emptySigneeIndex >= 0) {
      signees[emptySigneeIndex] = {
        ...signees[emptySigneeIndex],
        ...toSigneeContactDetails(contact),
      };
    } else {
      signees.push({
        ...toSigneeContactDetails(contact),
        SigningMethod: Constants.SigningMethod.BankIDSE,
      });
    }
    updateContactPersonProperty({
      value: signees,
      contactPersonUuid: client.Uuid,
      property: "Signees",
    });
  };

  const setGTSigneeFromUpsales = (
    clientIndex: number,
    contact: CoreInterfaces.ContactPersonItem
  ) => {
    if (isFinalOrOutdatedVersion) {
      return;
    }
    const client = userDetails[clientIndex];
    updateContactPersonProperty({
      value: { ...client.GTSignee, ...toSigneeContactDetails(contact) },
      contactPersonUuid: client.Uuid,
      property: "GTSignee",
    });
  };

  const applyUpsalesCompany = (
    clientUuid: string,
    company: CoreInterfaces.CompanyDetails
  ) => {
    const values: Partial<CoreInterfaces.ClientDetails> = {
      ClientName: company.Name,
      ClientOrgNr: company.OrgNr,
      UpsalesCompanyId: company.CompanyId,
    };
    const clientType = getClientTypeFromUpsales(company);
    if (clientType) {
      values.ClientType = clientType;
    }
    (Object.keys(values) as Array<keyof CoreInterfaces.ClientDetails>).forEach(
      (property) =>
        updateContactPersonProperty({
          value: values[property],
          contactPersonUuid: clientUuid,
          property,
        })
    );
  };

  const searchClientInUpsales = async (
    client: CoreInterfaces.ClientDetails,
    anchorEl: HTMLElement
  ) => {
    const orgNr = client.ClientOrgNr?.trim() ?? "";
    if (!Constants.OrgNrRegex.test(orgNr)) {
      showSnackBar(t("General.ClientDetails.InvalidOrgNumber"), "warning");
      return;
    }

    setSearchingClientUuid(client.Uuid);
    try {
      const companies = await searchCompaniesByOrgNr(orgNr);
      if (companies.length === 0) {
        showSnackBar(t("General.ClientDetails.UpsalesClientNotFound"), "info");
      } else if (companies.length === 1) {
        applyUpsalesCompany(client.Uuid, companies[0]);
      } else {
        setUpsalesCompanyMatches({
          clientUuid: client.Uuid,
          anchorEl,
          companies,
        });
      }
    } catch {
      showSnackBar(t("General.ClientDetails.UpsalesSearchFailed"), "error");
    } finally {
      setSearchingClientUuid(undefined);
    }
  };
  const isClientDetailsQuestion =
    question.data.inputType === Constants.QuestionInputType.ClientDetails;
  let isClientDetailsQuestionFilled = false;
  if (isClientDetailsQuestion) {
    isClientDetailsQuestionFilled = isClientDetailsDataFilled(question);
  }

  const isDataIncomplete =
    isClientDetailsQuestion && !isClientDetailsQuestionFilled;

  function addNewClient(): void {
    if (
      userDetails.length < Constants.MaxClientsPerEngagement &&
      !isFinalOrOutdatedVersion
    ) {
      dispatch({
        type: Constants.AppStateActions.AddClientDetails,
      });
    }
  }

  function onFilledStateChange(isFilled: boolean) {
    if (isDisabled) {
      return;
    }
    const newQuestionDTO: DTOs.QuestionDTO = {
      ...question,
      state: {
        ...question.state,
        isFilled:
          question.data.userValue && isDataIncomplete ? false : isFilled,
      },
    };
    onChange && onChange(newQuestionDTO);
  }

  return (
    <article
      className={`gt-questionRow gt-questionRow--clientDetails
         ${
           !question.data.userValue ||
           question.data.userValue ===
             Constants.HelpfulConstants.PleaseSelect ||
           isDataIncomplete
             ? "gt-questionRow--isNotAnsweredState"
             : ""
         } 
            ${
              question.state.isFilled && isAllDataFilled
                ? "gt-questionRow--filledAnswerState"
                : ""
            }
            ${
              !question.state.isFilled && isAllDataFilled
                ? "gt-questionRow--unfilledAnswerState"
                : ""
            }`}
    >
      {!isFinalOrOutdatedVersion && isModalVisible && (
        <GtDialog gtDialogDTO={deleteDialogDTO}>
          <article> {t("General.ClientDetails.DeleteClientQuestion")}</article>
        </GtDialog>
      )}
      {userDetails.map((contactPerson, index) => (
        <section key={contactPerson.Uuid} className="gt-clientDetails">
          <div className="gt-clientDetails__header">
            <p>
              {t("General.ClientDetails.Client")} {index + 1}
            </p>
            <div className="gt-clientDetails__row-buttons">
              {index > 0 && (
                <Tooltip title={t("General.ClientDetails.TooltipDeleteClient")}>
                  <Button
                    data-testid={question.data.code + "DeleteClient"}
                    className={`gt-clientDetails__row-buttons-delete
                      ${
                        isFinalOrOutdatedVersion
                          ? "gt-clientDetails__row-buttons-delete--disabled"
                          : ""
                      }`}
                    onClick={() => {
                      setContactPersonToDeleteUuid(contactPerson.Uuid);
                      setIsModalVisible(true);
                    }}
                  >
                    <GtRemoveHomeIcon />
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>

          <div className="gt-clientDetails__row">
            <label>{t("General.ClientDetails.ClientName")} </label>
            <TextField
              value={contactPerson.ClientName ?? ""}
              className="gt-TextField"
              onChange={(event: any) =>
                updateContactPersonProperty({
                  value: event.target.value,
                  contactPersonUuid: contactPerson.Uuid,
                  property: "ClientName",
                })
              }
              placeholder={t("Options.ClientName")}
              disabled={isFinalOrOutdatedVersion || index === 0}
            />
            <section className="gt-questionRow__indicators"></section>
            <section className="gt-questionRow__comment"></section>
          </div>

          <div className="gt-clientDetails__row">
            <label>
              {contactPerson.ClientType !== Constants.ClientType.PrivatePerson
                ? t("General.ClientDetails.ClientOrgNumber")
                : t("General.ClientDetails.PrivateClientOrgNumber")}
            </label>
            <GtDebouncedInput
              value={contactPerson.ClientOrgNr ?? ""}
              className="gt-TextField"
              onChange={(value: any) => {
                updateContactPersonProperty({
                  value,
                  contactPersonUuid: contactPerson.Uuid,
                  property: "ClientOrgNr",
                });
                // A hand-edited number no longer identifies the Upsales client, so drop its contacts.
                if (index > 0) {
                  updateContactPersonProperty({
                    value: undefined,
                    contactPersonUuid: contactPerson.Uuid,
                    property: "UpsalesCompanyId",
                  });
                }
              }}
              placeholder={
                contactPerson.ClientType !== Constants.ClientType.PrivatePerson
                  ? t("General.ClientDetails.ClientOrgNumber")
                  : t("General.ClientDetails.PrivateClientOrgNumber")
              }
              // The input is uncontrolled; a new timestamp makes it pick up a number filled in from Upsales.
              updateTimestamp={`${lastVersionChangedDate}|${
                contactPerson.UpsalesCompanyId ?? ""
              }`}
              disabled={isFinalOrOutdatedVersion || index === 0}
            />
            <section className="gt-questionRow__indicators">
              {index > 0 && (
                <Tooltip
                  title={t("General.ClientDetails.SearchClientInUpsales")}
                >
                  <span>
                    <IconButton
                      data-testid={question.data.code + "SearchUpsales"}
                      aria-label={t(
                        "General.ClientDetails.SearchClientInUpsales"
                      )}
                      className="gt-clientDetails__search-upsales"
                      onClick={(event) =>
                        searchClientInUpsales(
                          contactPerson,
                          event.currentTarget
                        )
                      }
                      disabled={
                        isFinalOrOutdatedVersion || !!searchingClientUuid
                      }
                    >
                      {searchingClientUuid === contactPerson.Uuid ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SearchIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </section>
            <section className="gt-questionRow__comment"></section>
          </div>
          {index === 0 && (
            <>
              <div className="gt-clientDetails__row">
                <label>{t("General.ClientDetails.StreetAddress")} </label>
                <GtDebouncedInput
                  value={contactPerson.StreetAdress ?? ""}
                  className="gt-TextField"
                  onChange={(value: any) =>
                    updateContactPersonProperty({
                      value,
                      contactPersonUuid: contactPerson.Uuid,
                      property: "StreetAdress",
                    })
                  }
                  placeholder={t("General.ClientDetails.StreetAddress")}
                  updateTimestamp={lastVersionChangedDate}
                  disabled={isFinalOrOutdatedVersion || index === 0}
                />
                <section className="gt-questionRow__indicators"></section>
                <section className="gt-questionRow__comment"></section>
              </div>
              <div className="gt-clientDetails__row">
                <label>{t("General.ClientDetails.Address")} </label>
                <GtDebouncedInput
                  value={
                    formatSwedishPostalCodeInAddressLine(
                      contactPerson.Address,
                      getCompanyAddressCountry(
                        globalState.remoteData?.companyDetails
                      )
                    ) ?? ""
                  }
                  className="gt-TextField"
                  onChange={(value: any) =>
                    updateContactPersonProperty({
                      value,
                      contactPersonUuid: contactPerson.Uuid,
                      property: "Address",
                    })
                  }
                  placeholder={t("General.ClientDetails.Address")}
                  updateTimestamp={lastVersionChangedDate}
                  disabled={isFinalOrOutdatedVersion || index === 0}
                />
                <section className="gt-questionRow__indicators"></section>
                <section className="gt-questionRow__comment"></section>
              </div>
            </>
          )}
          <div className="gt-clientDetails__row">
            <div className="gt-clientDetails__row-title">
              <label>{t("General.ClientDetails.ClientType")}</label>
              <Tooltip
                className="gt-questionRow__title-tooltip"
                data-testid={question.data.code + "Tooltip"}
                title={
                  <span
                    className="gt-tootipHint"
                    data-testid={question.data.code + "TooltipHint"}
                    dangerouslySetInnerHTML={{
                      __html: t("General.ClientDetails.ClientTypeTooltip"),
                    }}
                  ></span>
                }
              >
                <IconButton>
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </div>
            <Select
              key={index}
              data-testid={question.data.code + "ClientType"}
              className="gt-Select"
              value={(contactPerson.ClientType as string) ?? ""}
              onChange={(event) =>
                updateContactPersonProperty({
                  value: event.target.value as Constants.ClientType,
                  contactPersonUuid: contactPerson.Uuid,
                  property: "ClientType",
                })
              }
              disabled={
                question.data.inputConstraints.nonEditableAnswer || isDisabled
              }
            >
              {question.data.options &&
                question.data.options.map((eachOption) => (
                  <MenuItem
                    data-testid={`option_question${question.data.code}_option${eachOption.key}`}
                    key={`option_question${question.data.code}_option${eachOption.key}`}
                    value={eachOption.key}
                  >
                    {eachOption.untranslatable
                      ? eachOption.value
                      : t(eachOption.value)}
                  </MenuItem>
                ))}
            </Select>
            <section className="gt-questionRow__indicators"></section>
            <section className="gt-questionRow__comment"></section>
          </div>

          {contactPerson.ClientType === Constants.ClientType.Company && (
            <div className="gt-clientDetails__signees">
              <label>{t("General.ClientDetails.AddSignees")}</label>
              {Array.isArray(contactPerson.Signees) &&
                contactPerson.Signees.map((signee, signeeIndex) => (
                  <div key={signeeIndex} className="gt-clientDetails__signee">
                    <TextField
                      data-testid={question.data.code + "FirstName"}
                      key={signeeIndex + contactPerson.Uuid}
                      value={signee.FirstName ?? ""}
                      className="gt-TextField"
                      onChange={(event: any) =>
                        updateSignee(
                          index,
                          signeeIndex,
                          "FirstName",
                          event.target.value
                        )
                      }
                      placeholder={t(
                        "General.ClientDetails.ClientSigneeFirstName"
                      )}
                      disabled={isFinalOrOutdatedVersion}
                    />

                    <TextField
                      data-testid={question.data.code + "LastName"}
                      key={contactPerson.Uuid + signeeIndex}
                      value={signee.LastName ?? ""}
                      className="gt-TextField"
                      onChange={(event: any) =>
                        updateSignee(
                          index,
                          signeeIndex,
                          "LastName",
                          event.target.value
                        )
                      }
                      placeholder={t(
                        "General.ClientDetails.ClientSigneeLastName"
                      )}
                      disabled={isFinalOrOutdatedVersion}
                    />

                    {isDigitalSigning && (
                      <>
                        <TextField
                          key={signeeIndex + contactPerson.Uuid + "Email"}
                          error={
                            !!signee.Email &&
                            !Constants.EmailRegex.test(signee.Email)
                          }
                          id="outlined-error"
                          value={signee.Email ?? ""}
                          className="gt-TextField"
                          onChange={(event: any) =>
                            updateSignee(
                              index,
                              signeeIndex,
                              "Email",
                              event.target.value
                            )
                          }
                          placeholder={t("General.ClientDetails.EmailSignee")}
                          disabled={isFinalOrOutdatedVersion}
                        />
                        <Tooltip
                          className="gt-questionRow__title-tooltip"
                          data-testid={question.data.code + "Tooltip"}
                          title={
                            <span
                              className="gt-tootipHint"
                              data-testid={question.data.code + "TooltipHint"}
                              dangerouslySetInnerHTML={{
                                __html: t(
                                  "General.ClientDetails.DigitalSigningMethodCounselingTooltip"
                                ),
                              }}
                            ></span>
                          }
                        >
                          <IconButton>
                            <InfoOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                        <Select
                          key={index}
                          data-testid={question.data.code}
                          className="gt-Select"
                          value={
                            (signee.SigningMethod as string) ??
                            Constants.SigningMethod.BankIDSE
                          }
                          onChange={(event: any) =>
                            updateSignee(
                              index,
                              signeeIndex,
                              "SigningMethod",
                              event.target.value
                            )
                          }
                          disabled={
                            question.data.inputConstraints.nonEditableAnswer ||
                            isDisabled
                          }
                        >
                          {Object.values(Constants.SigningMethod).map(
                            (statusItem) => {
                              const translationKey =
                                Constants.SigningMethodTranslationMap[
                                  statusItem as keyof typeof Constants.SigningMethodTranslationMap
                                ] || statusItem;
                              return (
                                <MenuItem key={statusItem} value={statusItem}>
                                  {t(`Options.SigningMethod.${translationKey}`)}
                                </MenuItem>
                              );
                            }
                          )}
                        </Select>
                      </>
                    )}

                    {contactPerson.Signees.length > 1 && (
                      <Tooltip
                        title={t(`General.ClientDetails.RemoveTooltip`)}
                        placement="right"
                      >
                        <Button
                          className="gt-clientDetails__delete-signee"
                          onClick={() => deleteSignee(index, signeeIndex)}
                          disabled={isFinalOrOutdatedVersion}
                        >
                          <PersonRemoveIcon />
                        </Button>
                      </Tooltip>
                    )}
                    {!isDigitalSigning && (
                      <>
                        <section className="gt-questionRow__indicators"></section>
                        <section className="gt-questionRow__comment"></section>
                      </>
                    )}
                  </div>
                ))}
              <Tooltip title={t("General.ClientDetails.AddSigneeTextButton")}>
                <Button
                  className={`gt-clientDetails__add-signee 
                        ${
                          isFinalOrOutdatedVersion
                            ? "gt-clientDetails__add-signee--disabled"
                            : ""
                        }`}
                  onClick={() => addSignee(index)}
                  disabled={isFinalOrOutdatedVersion}
                >
                  <PersonAddAlt1Icon />
                </Button>
              </Tooltip>
              <ClientContactPicker
                contacts={getUpsalesContacts(contactPerson, index)}
                isContactAdded={(contact) =>
                  (contactPerson.Signees ?? []).some((signee) =>
                    isSameContact(signee, contact)
                  )
                }
                onSelect={(contact) => addSigneeFromUpsales(index, contact)}
                isDisabled={isFinalOrOutdatedVersion}
                testId={question.data.code + "UpsalesContact"}
              />
            </div>
          )}

          {contactPerson.ClientType === Constants.ClientType.PrivatePerson && (
            <div className="gt-clientDetails__signees">
              <label>{t("General.ClientDetails.AddSignee")}</label>
              <div className="gt-clientDetails__signee">
                <TextField
                  key={contactPerson.Uuid + "GTSigneeFirstName"}
                  value={contactPerson.GTSignee.FirstName ?? ""}
                  className="gt-TextField"
                  onChange={(event: any) =>
                    updateGTSignee(index, "FirstName", event.target.value)
                  }
                  placeholder={t("General.ClientDetails.ClientSigneeFirstName")}
                  disabled={isFinalOrOutdatedVersion}
                />

                <TextField
                  key={contactPerson.Uuid + "GTSigneeLastName"}
                  value={contactPerson.GTSignee.LastName ?? ""}
                  className="gt-TextField"
                  onChange={(event: any) =>
                    updateGTSignee(index, "LastName", event.target.value)
                  }
                  placeholder={t("General.ClientDetails.ClientSigneeLastName")}
                  disabled={isFinalOrOutdatedVersion}
                />

                {isDigitalSigning && (
                  <>
                    <TextField
                      key={contactPerson.Uuid + "GTSigneeEmail"}
                      error={
                        !!contactPerson.GTSignee.Email &&
                        !Constants.EmailRegex.test(contactPerson.GTSignee.Email)
                      }
                      id="outlined-error"
                      value={contactPerson.GTSignee.Email ?? ""}
                      className="gt-TextField"
                      onChange={(event: any) =>
                        updateGTSignee(index, "Email", event.target.value)
                      }
                      placeholder={t("General.ClientDetails.EmailSignee")}
                      disabled={isFinalOrOutdatedVersion}
                    />
                    <Tooltip
                      className="gt-questionRow__title-tooltip"
                      data-testid={question.data.code + "Tooltip"}
                      title={
                        <span
                          className="gt-tootipHint"
                          data-testid={question.data.code + "TooltipHint"}
                          dangerouslySetInnerHTML={{
                            __html: t(
                              "General.ClientDetails.DigitalSigningMethodCouselingTooltip"
                            ),
                          }}
                        ></span>
                      }
                    >
                      <IconButton>
                        <InfoOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                    <Select
                      key={index}
                      data-testid={question.data.code}
                      className="gt-Select"
                      value={
                        (contactPerson.GTSignee.SigningMethod as string) ??
                        Constants.SigningMethod.BankIDSE
                      }
                      onChange={(event: any) =>
                        updateGTSignee(
                          index,
                          "SigningMethod",
                          event.target.value
                        )
                      }
                      disabled={
                        question.data.inputConstraints.nonEditableAnswer ||
                        isDisabled
                      }
                    >
                      {Object.values(Constants.SigningMethod).map(
                        (statusItem) => {
                          const translationKey =
                            Constants.SigningMethodTranslationMap[
                              statusItem as keyof typeof Constants.SigningMethodTranslationMap
                            ] || statusItem;
                          return (
                            <MenuItem key={statusItem} value={statusItem}>
                              {t(`Options.SigningMethod.${translationKey}`)}
                            </MenuItem>
                          );
                        }
                      )}
                    </Select>
                  </>
                )}
                {!isDigitalSigning && (
                  <>
                    <section className="gt-questionRow__indicators"></section>
                    <section className="gt-questionRow__comment"></section>
                  </>
                )}
              </div>
              <ClientContactPicker
                contacts={getUpsalesContacts(contactPerson, index)}
                isContactAdded={(contact) =>
                  isSameContact(contactPerson.GTSignee, contact)
                }
                onSelect={(contact) => setGTSigneeFromUpsales(index, contact)}
                isDisabled={isFinalOrOutdatedVersion}
                testId={question.data.code + "UpsalesContact"}
              />
            </div>
          )}

          <div className="gt-clientDetails__row-icons">
            <section className="gt-questionRow__comment"></section>
            <section className="gt-questionRow__comment"></section>

            {index === userDetails.length - 1 && (
              <>
                <section className="gt-questionRow__indicators">
                  <span className="gt-questionRow__indicators-icon">
                    <CircleRoundedIcon fontSize="small" />
                  </span>
                  <span className="gt-questionRow__indicators-iconFill">
                    {question.state.isFilled && isAllDataFilled ? (
                      <CheckBoxIcon
                        fontSize="small"
                        onClick={() =>
                          onFilledStateChange(!question.state.isFilled)
                        }
                      />
                    ) : (
                      <CheckBoxOutlinedBlankIcon
                        fontSize="small"
                        onClick={() =>
                          onFilledStateChange(!question.state.isFilled)
                        }
                      />
                    )}
                  </span>
                </section>
                <section className="gt-questionRow__comment"></section>
              </>
            )}
          </div>
        </section>
      ))}
      {canAddClient && (
        <section className="gt-clientDetails__addClient">
          <Button
            data-testid={question.data.code + "AddClient"}
            className="gt-clientDetails__addClient-button"
            startIcon={<AddHomeIcon />}
            onClick={addNewClient}
            disabled={
              isFinalOrOutdatedVersion ||
              userDetails.length >= Constants.MaxClientsPerEngagement
            }
          >
            {t("General.ClientDetails.AddAdditionalClient")}
          </Button>
          <Tooltip
            className="gt-questionRow__title-tooltip"
            data-testid={question.data.code + "AddClientTooltip"}
            title={
              <span className="gt-tootipHint">
                {t("General.ClientDetails.AddAdditionalClientTooltip")}
              </span>
            }
          >
            <IconButton>
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
        </section>
      )}
      <Menu
        anchorEl={upsalesCompanyMatches?.anchorEl}
        open={!!upsalesCompanyMatches}
        onClose={() => setUpsalesCompanyMatches(undefined)}
      >
        <MenuItem disabled>
          {t("General.ClientDetails.SelectUpsalesClient")}
        </MenuItem>
        {upsalesCompanyMatches?.companies.map((company) => (
          <MenuItem
            key={company.CompanyId}
            onClick={() => {
              applyUpsalesCompany(upsalesCompanyMatches.clientUuid, company);
              setUpsalesCompanyMatches(undefined);
            }}
          >
            {company.Name} ({company.OrgNr})
          </MenuItem>
        ))}
      </Menu>
    </article>
  );
}

export default QuestionClientDetails;
